/* Blog article bundle CTA. Spec: 2026-08-04-blog-article-product-cta-design.md */
if (!customElements.get('article-cta-bundle')) {
  customElements.define(
    'article-cta-bundle',
    class ArticleCtaBundle extends HTMLElement {
      constructor() {
        super();
        this.addButton = this.querySelector('[data-cta-add]');
        this.addLabel = this.querySelector('[data-cta-add-label]');
        this.errorEl = this.querySelector('[data-cta-error]');
        this.items = Array.from(this.querySelectorAll('[data-cta-item]'));
        this.defaultLabel = this.addLabel ? this.addLabel.textContent : '';
        this.cart = document.querySelector('mini-cart');
        this.busy = false;
      }

      connectedCallback() {
        if (!this.addButton) return;

        // Any item the theme could not make safely addable disables the whole flow.
        this.blocked = this.items.some((el) => el.dataset.addable !== 'true');
        if (this.blocked) {
          this.degradeToLink();
          return;
        }

        // The button is rendered `hidden` (Finding 2, review round 1) so a
        // visitor whose JS never runs at all — network failure, an
        // ad-blocker rule, no customElements support — never sees an inert,
        // focusable dead button; only the always-present PDP link works.
        // Reaching this line proves this exact component upgraded, so it's
        // now safe to reveal the interactive control.
        this.addButton.hidden = false;

        this.addEventListener('change', (e) => {
          if (e.target.matches('.article-cta__swatch-input')) {
            this.refreshState();
            this.updateOptionLegend(e.target);
          }
        });
        // Attached unconditionally, not only when enabled: base.css's
        // .button[aria-disabled=true] { pointer-events: none } is overridden
        // for this component (article-product-cta.css), specifically so a
        // mouse/touch click while disabled still reaches this handler and can
        // move focus to the first unresolved choice instead of doing nothing.
        this.addButton.addEventListener('click', this.onAdd.bind(this));
        this.refreshState();
      }

      /**
       * Replace the button with a plain PDP link when in-place adding cannot
       * work (any row has more than one option). Reuses the translated
       * "Choose your <hero>" copy the section already rendered into
       * data-choose-label on the link — never hardcode that string here.
       */
      degradeToLink() {
        const view = this.querySelector('.article-cta__view');
        if (view && this.addButton) {
          this.addButton.remove();
          view.classList.remove('button--tertiary');
          view.classList.add('button--full-width');
          if (view.dataset.chooseLabel) view.textContent = view.dataset.chooseLabel;
        }
      }

      /**
       * Design v2: keeps the now-visible "COULEUR: value" legend
       * (article-cta-row.liquid's [data-cta-option-value]) in sync with the
       * swatch the customer just picked. Reads the newly-checked swatch's own
       * `title` attribute — already the variant title, rendered server-side —
       * instead of duplicating that data here. Display-only: does not touch
       * refreshState()'s readiness gate.
       */
      updateOptionLegend(input) {
        const fieldset = input.closest('[data-cta-options]');
        const legendValue = fieldset && fieldset.querySelector('[data-cta-option-value]');
        const label = input.nextElementSibling;
        if (legendValue && label) legendValue.textContent = label.getAttribute('title') || '';
      }

      /** Selected variant id per item, or null when a required choice is pending. */
      selectedVariants() {
        return this.items.map((el) => {
          if (el.dataset.variantId) return el.dataset.variantId;
          // A disabled (unavailable) radio can never be :checked from user
          // interaction, so this naturally excludes unavailable variants
          // without needing to special-case them here.
          const checked = el.querySelector('.article-cta__swatch-input:checked');
          return checked ? checked.value : null;
        });
      }

      refreshState() {
        const ready = this.selectedVariants().every(Boolean);
        this.addButton.setAttribute('aria-disabled', ready ? 'false' : 'true');
      }

      onAdd(evt) {
        evt.preventDefault();
        if (this.busy) return;
        if (this.addButton.getAttribute('aria-disabled') === 'true') {
          // Move focus to the first unresolved choice instead of failing silently.
          // :not(:disabled) matters: an unavailable variant can render first in
          // DOM order (this store currently has two Voyager variants out of
          // stock), and .focus() on a disabled input is a silent no-op.
          const pending = this.items.find(
            (el) => !el.dataset.variantId && !el.querySelector('.article-cta__swatch-input:checked')
          );
          const firstInput = pending && pending.querySelector('.article-cta__swatch-input:not(:disabled)');
          if (firstInput) firstInput.focus();
          return;
        }

        // `committed` flips true only once the server confirms the add
        // succeeded. From that point on, the shared .catch() below must never
        // present a throw to the customer as a failed add — a completed cart
        // mutation can't be undone by a broken UI refresh, a third-party
        // dataLayer shim, or a renamed `publish` global. This is deliberately
        // structural rather than a per-statement try/catch: it protects every
        // statement between the success check and the end of this method,
        // including ones not yet written, instead of only the one that broke
        // last time.
        //
        // Everything else — including the synchronous preamble that used to
        // sit outside the promise chain (busy/loading state, fetchConfig())
        // — now runs inside Promise.resolve().then(...), so ANY throw
        // anywhere in this method still reaches the single .catch()/.finally()
        // instead of escaping before .finally() exists and stranding the
        // button on "Adding…" forever.
        let committed = false;

        Promise.resolve()
          .then(() => {
            this.busy = true;
            this.setError(false);
            this.addButton.classList.add('loading');
            if (this.addLabel) this.addLabel.textContent = this.addButton.dataset.addingLabel || this.addLabel.textContent;

            const ids = this.selectedVariants();
            const attrConfig = fetchConfig('javascript');
            attrConfig.body = JSON.stringify({
              attributes: { blog_cta_source: this.dataset.articleHandle || '' },
            });

            // Attribute first, so blog origin is recorded even if the add later fails.
            return fetch(this.dataset.cartUpdateUrl, attrConfig).then((r) => {
              // Never blocks the add on attribution failure (the customer's
              // purchase matters more than the tracking attribute), but a
              // non-2xx here previously failed completely silently — fetch()
              // only rejects on network-level failure, not on HTTP error status.
              if (!r.ok) console.warn(`article-cta-bundle: blog_cta_source attribute update failed (HTTP ${r.status})`);
              return ids;
            });
          })
          .then((ids) => {
            const addConfig = fetchConfig('javascript');
            const body = {
              items: ids.map((id) => ({ id: Number(id), quantity: 1 })),
            };
            if (this.cart) {
              body.sections = this.cart.getSectionsToRender().map((s) => s.id);
              body.sections_url = window.location.pathname;
              this.cart.setActiveElement(document.activeElement);
            }
            addConfig.body = JSON.stringify(body);
            return fetch(this.dataset.cartAddUrl, addConfig)
              .then((r) => r.json())
              .then((response) => ({ response, ids }));
          })
          .then(({ response, ids }) => {
            if (response.status) throw new Error(response.description || response.message);

            // The server-side add is confirmed successful as of this line.
            committed = true;

            this.dispatchEvent(
              new CustomEvent('camstrap:blog_cta', {
                bubbles: true,
                detail: { placement: 'bundle', article: this.dataset.articleHandle, variants: ids },
              })
            );
            if (window.dataLayer) {
              window.dataLayer.push({
                event: 'blog_cta_add',
                placement: 'bundle',
                article: this.dataset.articleHandle,
              });
            }
            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartUpdate, { source: 'article-cta-bundle', cart: response });
            }
            if (this.cart) this.cart.renderContents(response);
            else window.location = theme.routes.cart_url;
          })
          .catch((e) => {
            console.error(e);
            if (committed) {
              // A completed cart mutation must never be shown to the customer
              // as a failed add (that invites a double purchase). Whatever
              // broke here is a UI/analytics problem downstream of a real
              // success — fall back to the real cart page, which reflects the
              // true state, instead of surfacing an error.
              window.location = theme.routes.cart_url;
            } else {
              this.setError(true);
            }
          })
          .finally(() => {
            this.busy = false;
            this.addButton.classList.remove('loading');
            if (this.addLabel) this.addLabel.textContent = this.defaultLabel;
          });
      }

      /**
       * The live region starts empty and gets its text on failure. Toggling a
       * hidden class on already-present text does not reliably re-announce;
       * inserting text into an empty aria-live region does.
       */
      setError(on) {
        if (!this.errorEl) return;
        this.errorEl.textContent = on ? this.errorEl.dataset.errorText || '' : '';
      }
    }
  );
}
